import { useState, useCallback, useEffect, useRef } from "react";
import { formatFileSize, createFileUploadId } from "@sumprio/ui";
import type { FileUploadFile } from "@sumprio/ui";
import { getFileTypeIcon } from "../Documents/utils";

const PROGRESS_INTERVAL_MS = 300;
const PROGRESS_STEP = 10;

export interface UseFileUploadWithProgressOptions {
  acceptedFileTypes: string[];
  maxFileSizeBytes?: number;
  maxFiles?: number;
  singleFileMode?: boolean;
  onValidationError?: (_message: string) => void;
}

export interface UseFileUploadWithProgressReturn {
  files: FileUploadFile[];
  fileMapRef: React.MutableRefObject<Map<string, File>>;
  handleFileSelect: (_fileList: FileList) => void;
  handleFileDelete: (_id: string) => void;
  hasCompletedFile: boolean;
  atFileLimit: boolean;
  resetFiles: () => void;
  getCompletedFile: () => File | null;
  getCompletedFiles: () => File[];
}

export function useFileUploadWithProgress(
  options: UseFileUploadWithProgressOptions
): UseFileUploadWithProgressReturn {
  const {
    acceptedFileTypes,
    maxFileSizeBytes,
    maxFiles,
    singleFileMode = true,
    onValidationError,
  } = options;

  const [files, setFiles] = useState<FileUploadFile[]>([]);
  const fileMapRef = useRef<Map<string, File>>(new Map());

  const hasCompletedFile = files.some((f) => f.status === "completed");
  const atFileLimit =
    (singleFileMode === true && hasCompletedFile) ||
    (maxFiles !== undefined && maxFiles !== null && files.length >= maxFiles);

  const handleFileSelect = useCallback(
    (fileList: FileList) => {
      if (singleFileMode === true && hasCompletedFile) return;
      const currentCount = files.length;
      const maxAllowed = singleFileMode === true ? 1 : maxFiles;
      if (maxAllowed !== undefined && maxAllowed !== null && currentCount >= maxAllowed) {
        onValidationError?.(`You can upload at most ${maxAllowed} files at a time.`);
        return;
      }
      const list = Array.from(fileList ?? []);
      const toAdd: FileUploadFile[] = [];
      let skippedDueToLimit = false;
      for (const file of list) {
        if (singleFileMode === true && (hasCompletedFile || toAdd.length >= 1)) break;
        if (
          maxAllowed !== undefined &&
          maxAllowed !== null &&
          currentCount + toAdd.length >= maxAllowed
        ) {
          skippedDueToLimit = true;
          break;
        }
        if (
          maxFileSizeBytes !== undefined &&
          maxFileSizeBytes !== null &&
          file.size > maxFileSizeBytes
        ) {
          onValidationError?.("File size must be within the allowed limit.");
          continue;
        }
        const ext = file.name.split(".").pop()?.toUpperCase() ?? "";
        if (!acceptedFileTypes.includes(ext)) {
          onValidationError?.(
            `Allowed types: ${acceptedFileTypes.map((e) => `.${e.toLowerCase()}`).join(", ")}`
          );
          continue;
        }
        const id = createFileUploadId();
        fileMapRef.current.set(id, file);
        toAdd.push({
          id,
          fileName: file.name,
          fileSize: formatFileSize(file.size),
          status: "inProgress",
          progress: 0,
          fileTypeIcon: getFileTypeIcon(file.name),
        });
      }
      if (skippedDueToLimit && maxAllowed !== undefined && maxAllowed !== null) {
        onValidationError?.(`You can upload at most ${maxAllowed} files at a time.`);
      }
      if (toAdd.length > 0) setFiles((prev) => [...prev, ...toAdd]);
    },
    [
      acceptedFileTypes,
      files.length,
      maxFileSizeBytes,
      maxFiles,
      singleFileMode,
      hasCompletedFile,
      onValidationError,
    ]
  );

  useEffect(() => {
    const inProgress = files.filter((f) => f.status === "inProgress");
    if (inProgress.length === 0) return;
    const interval = setInterval(() => {
      setFiles((prev) =>
        prev.map((file) => {
          if (file.status !== "inProgress") return file;
          const nextProgress = Math.min((file.progress ?? 0) + PROGRESS_STEP, 100);
          if (nextProgress >= 100) {
            return {
              ...file,
              status: "completed",
              progress: 100,
              statusMessage: "Upload complete",
            };
          }
          return { ...file, progress: nextProgress };
        })
      );
    }, PROGRESS_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [files]);

  const handleFileDelete = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    fileMapRef.current.delete(id);
  }, []);

  const resetFiles = useCallback(() => {
    setFiles([]);
    fileMapRef.current.clear();
  }, []);

  const getCompletedFile = useCallback((): File | null => {
    const completed = files.find((f) => f.status === "completed");
    if (!completed) return null;
    return fileMapRef.current.get(completed.id) ?? null;
  }, [files]);

  const getCompletedFiles = useCallback((): File[] => {
    const completed = files.filter((f) => f.status === "completed");
    return completed
      .map((f) => fileMapRef.current.get(f.id))
      .filter((file): file is File => file !== null);
  }, [files]);

  return {
    files,
    fileMapRef,
    handleFileSelect,
    handleFileDelete,
    hasCompletedFile,
    atFileLimit,
    resetFiles,
    getCompletedFile,
    getCompletedFiles,
  };
}
